import React, { useState, useEffect } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-hot-toast';
import { CreditCardIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import Button from '../../ui/Button';
import { formatCurrency } from '../../../utils/formatters';

// Import RTK Query hooks
import { 
  useCreateFundCollectionIntentMutation, 
  useConfirmFundCollectionMutation 
} from '../../../redux/apis/endpoints/paymentApi';

// Stripe card element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: false,
};

// Main fund collection form component
const MerchantFundCollectionForm = ({ 
  refundAmount, 
  currency, 
  fulfillmentGroupId,
  usageFee,
  onPaymentSuccess,
  onPaymentError 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  // RTK Query mutations
  const [createFundCollectionIntent] = useCreateFundCollectionIntentMutation();
  const [confirmFundCollection] = useConfirmFundCollectionMutation();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      toast.error('Stripe not loaded. Please refresh and try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error('Card element not found');
      return;
    }

    // Add debugging
    console.log('MerchantFundCollection handleSubmit - Starting with data:', {
      refundAmount,
      currency,
      fulfillmentGroupId,
      amount: refundAmount,
      currencyLower: currency.toLowerCase()
    });

    setIsProcessing(true);
    setPaymentError(null);

    try {
      // Create payment intent for merchant fund collection using RTK Query
      console.log('Creating refund fund payment intent...');
      const createIntentResult = await createFundCollectionIntent({
        amount: refundAmount,
        currency: currency.toLowerCase(),
        fulfillmentGroupId,
        description: `Refund fund payment for return approval - FG ${fulfillmentGroupId}`
      }).unwrap();

      console.log('Refund fund payment intent created:', createIntentResult);
      const { clientSecret, paymentIntentId } = createIntentResult;

      if (!clientSecret) {
        throw new Error('Failed to create payment intent');
      }

      // Confirm payment with Stripe
      console.log('Confirming payment with Stripe...');
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: 'Refund Fund Payment',
          },
        },
      });

      if (confirmError) {
        console.error('Stripe payment confirmation error:', confirmError);
        throw new Error(confirmError.message);
      }

      console.log('Stripe payment confirmed:', paymentIntent);

      if (paymentIntent.status === 'succeeded') {
        // Confirm with backend that payment succeeded using RTK Query
        console.log('Confirming with backend...');
        const confirmResult = await confirmFundCollection({
          paymentIntentId,
          fulfillmentGroupId,
          usageFee
        }).unwrap();

        console.log('Backend confirmation result:', confirmResult);

        if (confirmResult.success) {
          toast.success('Refund payment completed successfully!');
          onPaymentSuccess({
            paymentIntentId,
            amount: refundAmount,
            currency,
            status: 'succeeded'
          });
        } else {
          throw new Error('Failed to confirm payment with backend');
        }
      } else {
        throw new Error('Payment not completed');
      }

    } catch (error) {
      console.error('Refund fund payment error:', error);
      
      // Handle RTK Query errors specifically
      let errorMessage = 'Payment failed';
      if (error.data) {
        // RTK Query error with backend response
        errorMessage = error.data.error || error.data.message || 'Backend error occurred';
      } else if (error.message) {
        // Regular error or Stripe error
        errorMessage = error.message;
      }
      
      console.error('Parsed error message:', errorMessage);
      setPaymentError(errorMessage);
      onPaymentError(error);
      toast.error(`Payment failed: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCardChange = (event) => {
    setPaymentError(event.error ? event.error.message : null);
    setCardComplete(event.complete);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Pay Refund Amount</h3>
      </div>

      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Refund Amount Payment Required</p>
            <p className="mt-1">
              You need to pay <strong>{formatCurrency(refundAmount, currency)}</strong> to approve this return request.
              This amount will be used to refund the customer when they return the items.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="card-element" className="block text-sm font-medium text-gray-700 mb-2">
            Payment Method
          </label>
          <div className="border border-gray-300 rounded-md px-3 py-2 bg-white">
            <CardElement
              id="card-element"
              options={cardElementOptions}
              onChange={handleCardChange}
            />
          </div>
          {paymentError && (
            <p className="mt-2 text-sm text-red-600">{paymentError}</p>
          )}
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Refund amount to pay: <span className="font-medium">{formatCurrency(refundAmount, currency)}</span>
          </div>
          <Button
            type="submit"
            variant="primary"
            disabled={!stripe || !cardComplete || isProcessing}
            isLoading={isProcessing}
            className="min-w-[140px]"
          >
            {isProcessing ? 'Processing...' : `Pay ${formatCurrency(refundAmount, currency)}`}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Wrapper component with Stripe Elements provider
const MerchantFundCollection = ({ 
  stripePromise, 
  refundAmount, 
  currency, 
  fulfillmentGroupId,
  usageFee,
  onPaymentSuccess,
  onPaymentError,
  show = true
}) => {
  // Add safety checks and debugging
  console.log('MerchantFundCollection props:', {
    refundAmount,
    currency,
    fulfillmentGroupId,
    show
  });

  // Don't render if required props are missing
  if (!show || refundAmount <= 0 || !fulfillmentGroupId) {
    console.log('MerchantFundCollection not rendering:', {
      show,
      refundAmount,
      fulfillmentGroupId: !!fulfillmentGroupId
    });
    return null;
  }

  return (
    <Elements stripe={stripePromise}>
      <MerchantFundCollectionForm
        refundAmount={refundAmount}
        currency={currency}
        fulfillmentGroupId={fulfillmentGroupId}
        usageFee={usageFee}
        onPaymentSuccess={onPaymentSuccess}
        onPaymentError={onPaymentError}
      />
    </Elements>
  );
};

export default MerchantFundCollection; 